import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronRight, ArrowLeft, ChevronDown, Save, Brain, Moon, Sun, Lock, Loader } from 'lucide-react';

const DailyTraining = () => {
    const [password, setPassword] = useState('');
    const [authenticated, setAuthenticated] = useState(false);
    const [questions, setQuestions] = useState({});
    const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
    const [response, setResponse] = useState('');
    const [sessionType, setSessionType] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [sessionDate] = useState(new Date().toISOString().split('T')[0]);
    const [completedCategories, setCompletedCategories] = useState([]);
    const [expandedSection, setExpandedSection] = useState(null);

    // Check if user was previously authenticated in this session
    useEffect(() => {
        const savedAuth = localStorage.getItem('trainingAuthenticated');
        const savedSessionType = localStorage.getItem('trainingSessionType');
        if (savedAuth === 'true' && savedSessionType) {
            setAuthenticated(true);
            setSessionType(savedSessionType);

            const savedQuestions = localStorage.getItem(`questions_${savedSessionType}_${sessionDate}`);
            if (savedQuestions) {
                setQuestions(JSON.parse(savedQuestions));
            } else {
                fetchQuestions(savedSessionType);
            }

            const savedCompletedCategories = localStorage.getItem(`completed_${savedSessionType}_${sessionDate}`);
            if (savedCompletedCategories) {
                setCompletedCategories(JSON.parse(savedCompletedCategories));
            }

            const savedCurrentIndex = localStorage.getItem(`currentIndex_${savedSessionType}_${sessionDate}`);
            if (savedCurrentIndex) {
                setCurrentCategoryIndex(parseInt(savedCurrentIndex));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per session date, not on every render
    }, [sessionDate]);

    const handleAuth = async () => {
        setIsLoading(true);
        setErrorMessage('');

        try {
            const authResponse = await axios.post('/api/verifyPassword', {
                password,
                sessionType
            });

            if (authResponse.data.success) {
                setAuthenticated(true);
                localStorage.setItem('trainingAuthenticated', 'true');
                localStorage.setItem('trainingSessionType', sessionType);
                fetchQuestions(sessionType);
            } else {
                setErrorMessage('Invalid password');
            }
        } catch (error) {
            console.error("Authentication error:", error);
            setErrorMessage('Authentication failed');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchQuestions = async (type) => {
        setIsLoading(true);
        try {
            // First check if we have cached questions for today's session
            const cachedQuestions = localStorage.getItem(`questions_${type}_${sessionDate}`);
            if (cachedQuestions) {
                setQuestions(JSON.parse(cachedQuestions));
                setIsLoading(false);
                return;
            }

            const response = await axios.post('/api/generateQuestions', { sessionType: type });
            setQuestions(response.data);

            // Cache the questions for today's session
            localStorage.setItem(`questions_${type}_${sessionDate}`, JSON.stringify(response.data));
        } catch (error) {
            console.error("Error fetching questions:", error);
            setErrorMessage('Failed to fetch questions. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleNextQuestion = () => {
        // Save current response
        if (response.trim()) {
            saveResponse();
        }

        const categoryKeys = Object.keys(questions);
        if (currentCategoryIndex < categoryKeys.length - 1) {
            const newIndex = currentCategoryIndex + 1;
            setCurrentCategoryIndex(newIndex);
            setResponse('');

            // Save current index
            localStorage.setItem(`currentIndex_${sessionType}_${sessionDate}`, newIndex.toString());

            // Load any previously saved response for the next question
            const category = categoryKeys[newIndex];
            const savedResponse = localStorage.getItem(`response_${sessionType}_${sessionDate}_${category}`);
            if (savedResponse) {
                setResponse(savedResponse);
            }
        }
    };

    const handlePreviousQuestion = () => {
        // Save current response
        if (response.trim()) {
            saveResponse();
        }

        if (currentCategoryIndex > 0) {
            const newIndex = currentCategoryIndex - 1;
            setCurrentCategoryIndex(newIndex);

            // Save current index
            localStorage.setItem(`currentIndex_${sessionType}_${sessionDate}`, newIndex.toString());

            // Load any previously saved response for the previous question
            const categoryKeys = Object.keys(questions);
            const category = categoryKeys[newIndex];
            const savedResponse = localStorage.getItem(`response_${sessionType}_${sessionDate}_${category}`);
            if (savedResponse) {
                setResponse(savedResponse);
            } else {
                setResponse('');
            }
        }
    };

    const saveResponse = () => {
        if (!response.trim()) return;

        const categoryKeys = Object.keys(questions);
        const category = categoryKeys[currentCategoryIndex];

        // Save locally
        localStorage.setItem(`response_${sessionType}_${sessionDate}_${category}`, response);

        // Mark as completed
        if (!completedCategories.includes(category)) {
            const newCompleted = [...completedCategories, category];
            setCompletedCategories(newCompleted);
            localStorage.setItem(`completed_${sessionType}_${sessionDate}`, JSON.stringify(newCompleted));
        }
    };

    const pushToGitHub = async () => {
        if (!response.trim()) return;

        setIsLoading(true);
        saveResponse();

        const categoryKeys = Object.keys(questions);
        const category = categoryKeys[currentCategoryIndex];
        const question = questions[category];

        const content = `## Date: ${new Date().toISOString().split('T')[0]}\n## Session: ${sessionType === 'morning' ? 'Morning (Structure & Control)' : 'Night (Debug & Reinforce)'}\n## Category: ${category}\n\n## Question: ${question}\n\n**Response:**\n${response}\n\n---\n`;
        const fileName = `responses/${sessionType}/${sessionDate}-${category.toLowerCase().replace(/\s+/g, '-')}.md`;

        try {
            await axios.post('/api/saveToGitHub', { content, fileName });
            setErrorMessage('');
            alert("Response saved to GitHub successfully!");
        } catch (error) {
            console.error("Error pushing to GitHub:", error);
            // Display more specific error message to the user
            if (error.response && error.response.data && error.response.data.details) {
                setErrorMessage(`Failed to save to GitHub: ${error.response.data.details}`);
            } else {
                setErrorMessage('Failed to save to GitHub. Check console for details.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResponseChange = (e) => {
        setResponse(e.target.value);

        // Autosave after delay
        const categoryKeys = Object.keys(questions);
        const category = categoryKeys[currentCategoryIndex];
        localStorage.setItem(`response_${sessionType}_${sessionDate}_${category}`, e.target.value);
    };

    const resetSession = () => {
        // eslint-disable-next-line no-restricted-globals
        if (confirm("Are you sure you want to sign out? Your progress for today will still be saved.")) {
            setAuthenticated(false);
            setSessionType(null);
            localStorage.removeItem('trainingAuthenticated');
            localStorage.removeItem('trainingSessionType');
        }
    };

    const toggleSection = (index) => {
        setExpandedSection(expandedSection === index ? null : index);
    };

    const CategorySections = () => {
        const categoryKeys = Object.keys(questions);
        return (
            <div className="space-y-6 mb-12">
                {categoryKeys.map((category, index) => (
                    <div
                        key={category}
                        className="cursor-pointer border-b border-gray-200 pb-3"
                        onClick={() => {
                            toggleSection(index);
                            if (response.trim()) {
                                saveResponse();
                            }
                            setCurrentCategoryIndex(index);
                            localStorage.setItem(`currentIndex_${sessionType}_${sessionDate}`, index.toString());

                            const savedResponse = localStorage.getItem(`response_${sessionType}_${sessionDate}_${category}`);
                            if (savedResponse) {
                                setResponse(savedResponse);
                            } else {
                                setResponse('');
                            }
                        }}
                    >
                        <div className="flex items-start space-x-4 pl-2">
                            <span className="text-sm opacity-50 mt-1">
                                {(index + 1).toString().padStart(2, '0')}
                            </span>
                            <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                    <h3 className={`text-xl transition-opacity ${
                                        currentCategoryIndex === index ? 'font-bold' : 'font-normal'
                                    } ${completedCategories.includes(category) ? 'text-gray-400' : 'text-black'}`}>
                                        {category}
                                    </h3>
                                    <ChevronDown
                                        className={`w-4 h-4 transition-transform duration-300 ${expandedSection === index ? 'rotate-180' : ''}`}
                                    />
                                </div>
                                {expandedSection === index && (
                                    <p className="text-gray-600 leading-relaxed mt-2">
                                        {questions[category]}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const allCompleted = completedCategories.length === Object.keys(questions).length && Object.keys(questions).length > 0;

    return (
        <div className="bg-white min-h-screen w-full font-mono text-black">
            <div className="max-w-6xl mx-auto p-12">
                {!authenticated ? (
                    <div className="max-w-xl mx-auto">
                        <h1 className="text-4xl mb-12">MENTAL TRAINING</h1>
                        <p className="text-2xl font-bold mb-8">Daily system recalibration.</p>
                        <p className="text-lg max-w-3xl leading-relaxed text-gray-700 mb-16">
                            {sessionType === 'morning'
                                ? "Morning = Structure & Control. You don't wake up and react to the world. You wake up and shape it."
                                : sessionType === 'night'
                                    ? "Night = System Debugging. Reflection, reinforcement, deep restructuring."
                                    : "Choose your training protocol to begin the daily mental recalibration."}
                        </p>

                        <div className="space-y-6 mb-16">
                            <button
                                onClick={() => setSessionType('morning')}
                                className={`block w-full text-left px-4 py-6 border hover:border-black transition-colors ${
                                    sessionType === 'morning' ? 'border-black' : 'border-gray-200'
                                }`}
                            >
                                <div className="flex items-center">
                                    <Sun className="mr-4" size={24} />
                                    <div>
                                        <h3 className="text-xl font-bold">Morning Protocol</h3>
                                        <p className="text-gray-600">Structure & Control</p>
                                    </div>
                                </div>
                            </button>

                            <button
                                onClick={() => setSessionType('night')}
                                className={`block w-full text-left px-4 py-6 border hover:border-black transition-colors ${
                                    sessionType === 'night' ? 'border-black' : 'border-gray-200'
                                }`}
                            >
                                <div className="flex items-center">
                                    <Moon className="mr-4" size={24} />
                                    <div>
                                        <h3 className="text-xl font-bold">Night Protocol</h3>
                                        <p className="text-gray-600">Debug & Reinforce</p>
                                    </div>
                                </div>
                            </button>
                        </div>

                        {sessionType && (
                            <div className="mb-16">
                                <div className="mb-8">
                                    <h2 className="text-2xl mb-6">Access Verification:</h2>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full p-4 text-black border border-gray-300 focus:border-black outline-none transition-colors"
                                        placeholder="Enter access key"
                                        onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
                                    />
                                </div>

                                <button
                                    onClick={handleAuth}
                                    disabled={isLoading || !password}
                                    className={`w-full py-4 px-6 flex items-center justify-center transition-colors ${
                                        isLoading || !password
                                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                            : 'bg-black text-white hover:bg-gray-800'
                                    }`}
                                >
                                    {isLoading ? (
                                        <Loader className="animate-spin mr-2" size={20} />
                                    ) : (
                                        <Lock className="mr-2" size={20} />
                                    )}
                                    Begin Protocol
                                </button>

                                {errorMessage && (
                                    <p className="mt-4 text-red-500">{errorMessage}</p>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <div className="flex items-center mb-12">
                            <button
                                onClick={resetSession}
                                className="inline-flex items-center space-x-2 text-sm hover:opacity-70 transition-opacity"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                <span>Exit Protocol</span>
                            </button>
                            <div className="ml-auto text-sm text-gray-500">
                                {sessionDate} | {sessionType === 'morning' ? 'MORNING PROTOCOL' : 'NIGHT PROTOCOL'}
                            </div>
                        </div>

                        <h1 className="text-4xl mb-8">
                            {sessionType === 'morning' ? 'MORNING TRAINING' : 'NIGHT TRAINING'}
                        </h1>

                        <p className="text-lg max-w-3xl leading-relaxed text-gray-700 mb-16">
                            {sessionType === 'morning'
                                ? "You don't wake up and react to the world. You wake up and reprogram yourself."
                                : "System Debugging: Reflection, reinforcement, deep restructuring."}
                        </p>

                        {isLoading && Object.keys(questions).length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16">
                                <Loader className="animate-spin mb-4" size={32} />
                                <p>Generating training protocol...</p>
                            </div>
                        ) : Object.keys(questions).length > 0 ? (
                            <>
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                                    {/* Left side: Categories list */}
                                    <div className="lg:col-span-1">
                                        <h2 className="text-2xl mb-6">Training Modules:</h2>
                                        <div className="mb-6 bg-gray-100 h-1 w-full">
                                            <div
                                                className="h-full bg-black"
                                                style={{ width: `${(completedCategories.length / Object.keys(questions).length) * 100}%` }}
                                            ></div>
                                        </div>
                                        <p className="text-sm text-gray-500 mb-6">
                                            {completedCategories.length} / {Object.keys(questions).length} completed
                                        </p>

                                        <CategorySections />
                                    </div>

                                    {/* Right side: Current question and response */}
                                    <div className="lg:col-span-2">
                                        <div className="mb-6">
                                            <h2 className="text-2xl mb-4">{Object.keys(questions)[currentCategoryIndex]}</h2>
                                            <p className="text-lg text-gray-700 leading-relaxed mb-8">
                                                {questions[Object.keys(questions)[currentCategoryIndex]]}
                                            </p>

                                            <textarea
                                                className="w-full p-4 text-black border border-gray-300 focus:border-black outline-none transition-colors mb-8"
                                                rows="10"
                                                value={response}
                                                onChange={handleResponseChange}
                                                placeholder="Write your response here..."
                                            />

                                            {errorMessage && (
                                                <p className="mb-6 text-red-500">{errorMessage}</p>
                                            )}

                                            <div className="flex items-center justify-between">
                                                <button
                                                    onClick={handlePreviousQuestion}
                                                    disabled={currentCategoryIndex === 0}
                                                    className={`py-3 px-6 flex items-center justify-center transition-colors ${
                                                        currentCategoryIndex === 0
                                                            ? 'text-gray-400 cursor-not-allowed'
                                                            : 'hover:text-gray-600'
                                                    }`}
                                                >
                                                    <ArrowLeft className="mr-2" size={16} />
                                                    Previous
                                                </button>

                                                <button
                                                    onClick={pushToGitHub}
                                                    disabled={!response.trim() || isLoading}
                                                    className={`py-3 px-6 flex items-center justify-center transition-colors ${
                                                        !response.trim() || isLoading
                                                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                            : 'bg-black text-white hover:bg-gray-800'
                                                    }`}
                                                >
                                                    {isLoading ? (
                                                        <Loader className="animate-spin mr-2" size={16} />
                                                    ) : (
                                                        <Save className="mr-2" size={16} />
                                                    )}
                                                    Save Response
                                                </button>

                                                <button
                                                    onClick={handleNextQuestion}
                                                    disabled={currentCategoryIndex >= Object.keys(questions).length - 1}
                                                    className={`py-3 px-6 flex items-center justify-center transition-colors ${
                                                        currentCategoryIndex >= Object.keys(questions).length - 1
                                                            ? 'text-gray-400 cursor-not-allowed'
                                                            : 'hover:text-gray-600'
                                                    }`}
                                                >
                                                    Next
                                                    <ChevronRight className="ml-2" size={16} />
                                                </button>
                                            </div>
                                        </div>

                                        {allCompleted && (
                                            <div className="mt-12 p-8 border border-gray-200">
                                                <h3 className="text-2xl font-bold mb-4">Protocol Complete</h3>
                                                <p className="text-gray-700">
                                                    You've completed all training modules for this session. Your responses have been recorded.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-16">
                                <button
                                    onClick={() => fetchQuestions(sessionType)}
                                    className="bg-black text-white hover:bg-gray-800 px-8 py-4 text-xl flex items-center transition-colors">
                                    <Brain className="mr-3" size={24} />
                                    Initialize Today's Protocol
                                </button>
                            </div>
                        )}
                    </div>
                )}
                <footer className="mt-24 text-sm text-gray-500">
                    © Exit Death Mental Training System
                </footer>
            </div>
        </div>
    );
};

export default DailyTraining;